/**
 * Australian-specific constants for forms and dropdowns
 */

export const AUSTRALIAN_STATES = [
  { value: 'NSW', label: 'New South Wales' },
  { value: 'VIC', label: 'Victoria' },
  { value: 'QLD', label: 'Queensland' },
  { value: 'WA', label: 'Western Australia' },
  { value: 'SA', label: 'South Australia' },
  { value: 'TAS', label: 'Tasmania' },
  { value: 'ACT', label: 'Australian Capital Territory' },
  { value: 'NT', label: 'Northern Territory' }
] as const;

/**
 * `shortLabel` is the role as a list or a one-line choice names it (an
 * enquiry's LPRs, VALE-2061): the person holds the role whether or not a grant
 * has issued, and "Administrator" is what the firm calls them on the phone.
 * `label` stays the formal wording the matter's own role picker uses.
 */
export const LEGAL_PERSONAL_REPRESENTATIVE_ROLES = [
  { value: 'executor', label: 'Executor', shortLabel: 'Executor' },
  {
    value: 'applicant_for_letters_of_administration',
    label: 'Applicant for Letters of Administration',
    shortLabel: 'Administrator'
  }
] as const;

/**
 * Reasons a named executor (LPR) is non-proving. Proving vs non-proving is an
 * Australia-wide common-law concept: where a will names multiple co-executors,
 * the proving executors apply for the grant and any named executor not part of
 * that application is non-proving. Every non-proving executor is non-proving
 * for exactly one of these reasons; "leave reserved" is only one of them, not
 * a synonym for non-proving. See LF-5372 (mirrors the backend NonProvingReason
 * enum). `description` is surfaced as subtext when the reason picker is open.
 */
export const NON_PROVING_REASONS = [
  {
    value: 'leave_reserved',
    label: 'Leave reserved',
    description:
      'Steps back from acting now but keeps the right to prove the will later.'
  },
  {
    value: 'renunciation',
    label: 'Renunciation',
    description:
      'Permanently gives up the right to act; cannot step back in later.'
  },
  {
    value: 'predeceased',
    label: 'Predeceased',
    description: 'Died before the will-maker.'
  },
  {
    value: 'died_after_will_maker',
    label: 'Died after the will-maker',
    description:
      'Outlived the will-maker but died before the grant was taken out, so the right to prove ceases.'
  },
  {
    value: 'incapacity',
    label: 'Incapacity',
    description: 'Has lost the mental capacity to manage the estate.'
  },
  {
    value: 'passed_over',
    label: 'Passed over',
    description:
      'Bypassed by the court before a grant is made (conflict, unfitness, or cannot be located).'
  }
] as const;

export type NonProvingReason = (typeof NON_PROVING_REASONS)[number]['value'];

/** value → short pill/display label for a non-proving reason. */
export const NON_PROVING_REASON_LABELS = Object.fromEntries(
  NON_PROVING_REASONS.map((r) => [r.value, r.label])
) as Record<NonProvingReason, string>;

/**
 * The non-proving reasons that mean the executor has died. Picking one of these
 * also flags the contact deceased, so the two facts can never disagree on the
 * record — a lawyer who states an executor died should not then have to tick a
 * separate box saying the same thing.
 *
 * Bind to this rather than testing `=== 'predeceased'`. Both reasons are a
 * death; only the order relative to the will-maker's own death differs, and
 * that distinction belongs to entitlement substitution, not to whether the
 * person is alive.
 */
export function isDeathNonProvingReason(
  value: string | null | undefined
): boolean {
  return value === 'predeceased' || value === 'died_after_will_maker';
}

/**
 * Country data is NOT Australian-specific and lives in `./countries.ts` — the canonical
 * ISO 3166 name list, its `{ value, label }` option form, and `DEFAULT_COUNTRY`. Re-exported
 * here only so existing `@/lib/constants` importers keep working; import from
 * `./countries` directly in new code.
 */
export { DEFAULT_COUNTRY, COUNTRY_NAMES, COUNTRY_OPTIONS } from './countries';
export type { CountryName } from './countries';
